// src/components/TopBar.jsx
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { ThemeContext } from "../context/ThemeContext";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const AVATAR_SIZE = parseInt(process.env.REACT_APP_AVATAR_SIZE || "40", 10);
const DEFAULT_AVATAR = process.env.PUBLIC_URL + "/assets/img/admin profile.jpg";

// ---- endpoints ----
const pickEnv = (k) =>
  (typeof import.meta !== "undefined" && import.meta.env?.[k]) ||
  (typeof process !== "undefined" && process.env?.[k]) ||
  "";

const NOTIF_BY_USER_BASE = (
  pickEnv("REACT_APP_SYSTEM_NOTIF_BY_USER") ||
  pickEnv("VITE_SYSTEM_NOTIF_BY_USER") ||
  "https://backend.tabdhey.bt/admin/api/system-notifications"
).replace(/\/+$/, "");

// ---- utils ----
const isAbsoluteUrl = (u) => /^https?:\/\//i.test(u || "");
const joinUrl = (b, p) =>
  `${String(b).replace(/\/+$/, "")}/${String(p).replace(/^\/+/, "")}`;
const resolveImageUrl = (raw) =>
  raw ? (isAbsoluteUrl(raw) ? raw : joinUrl(API_BASE_URL, raw)) : "";

function readRoleKind(user = {}) {
  const raw = user.role ?? user.user_role ?? user.role_name ?? user.type ?? "";
  const norm = String(raw).toLowerCase().replace(/\s+/g, " ").trim();
  const compact = norm.replace(/\s+/g, "");
  const isSuperByString = norm === "super admin" || compact === "superadmin";
  const isAdminByString = norm === "admin" || isSuperByString;
  const isSuperByFlags =
    user.is_super === true || user.is_super_admin === true ||
    user.super === true || user.super_admin === true ||
    user.is_super === 1  || user.is_super_admin === 1;
  const isAdminByFlags = user.is_admin === true || user.is_admin === 1;
  const isSuper   = isSuperByString || isSuperByFlags;
  const isAdmin   = isAdminByString || isAdminByFlags || isSuper;
  const isFinance = norm === "finance" || localStorage.getItem("is_finance") === "true";
  const roleLabel = isSuper ? "Super Admin" : isFinance ? "Finance" : "Admin";
  return { isSuper, isAdmin, isFinance, roleLabel };
}

const ROLE_BADGE_COLOR = {
  "Super Admin": { bg: "#0d6efd", text: "#fff" },
  "Finance":     { bg: "#198754", text: "#fff" },
  "Admin":       { bg: "#6c757d", text: "#fff" },
};

// ---- time + seen helpers ----
const SEEN_KEY = (uid) => `admin_notif_seen_ts_${uid}`;

// Robust parse for "YYYY-MM-DD HH:mm:ss" (local) and ISO strings
const parseTime = (t) => {
  if (!t) return 0;
  const s = String(t).trim();

  // Try ISO first (space -> T)
  const isoish = s.includes("T") ? s : s.replace(" ", "T");
  const d1 = new Date(isoish);
  if (!Number.isNaN(d1.getTime())) return d1.getTime();

  // Fallback: strict "YYYY-MM-DD HH:mm:ss" local
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/.exec(s);
  if (m) {
    const [, Y, M, D, h, mi, sec] = m.map(Number);
    const d2 = new Date(Y, M - 1, D, h, mi, sec); // local timezone
    if (!Number.isNaN(d2.getTime())) return d2.getTime();
  }
  return 0;
};

const receivedLine = (t) => {
  const ms = parseTime(t);
  if (!ms) return "";
  const d = new Date(ms);
  return `Received at ${d.toLocaleString()}`;
};

// Human-friendly time ago: minutes, hours, days, weeks, months, years
const timeAgo = (t) => {
  const ms = parseTime(t);
  if (!ms) return "";
  let diff = Date.now() - ms;
  if (diff < 0) diff = 0;

  const sec = Math.floor(diff / 1000);
  const min = Math.floor(sec / 60);
  const hour = Math.floor(min / 60);
  const day = Math.floor(hour / 24);
  const week = Math.floor(day / 7);
  const month = Math.floor(day / 30);
  const year = Math.floor(day / 365);

  if (sec < 45) return "just now";
  if (min < 60) return `${min} ${min === 1 ? "minute" : "minutes"} ago`;
  if (hour < 24) return `${hour} ${hour === 1 ? "hour" : "hours"} ago`;
  if (day <= 7) return `${day} ${day === 1 ? "day" : "days"} ago`;
  if (week <= 4) return `${week} ${week === 1 ? "week" : "weeks"} ago`;
  if (year < 1) return month === 1 ? "1 month ago" : `${month} months ago`;
  return year === 1 ? "1 year ago" : `${year} years ago`;
};

// keep only items for admins; also respect recipient_id if present
function filterForThisAdmin(list = [], userId) {
  const isAdminRole = (v = "") => /^(admin|admins)$/i.test(String(v).trim());
  return list.filter((n) => {
    if (n.recipient_id && String(n.recipient_id) !== String(userId)) return false;

    const role = String(n.role || n.recipient_role || "").toLowerCase();
    if (role && !isAdminRole(role)) return false;

    const aud = n.target_audience ?? n.audience ?? [];
    if (Array.isArray(aud) && aud.length) return aud.some(isAdminRole);

    if (typeof aud === "string" && aud.trim()) {
      try {
        const arr = JSON.parse(aud);
        if (Array.isArray(arr)) return arr.some(isAdminRole);
      } catch {
        return aud
          .split(",")
          .map((s) => s.trim())
          .some(isAdminRole);
      }
    }
    return true; // already scoped by /:user_id
  });
}

const sortByNewest = (list = []) =>
  [...list].sort(
    (a, b) =>
      parseTime(b.created_at || b.sent_at) - parseTime(a.created_at || a.sent_at)
  );

// ---- popup renderer (NEW / EARLIER sections + numbering + labels) ----
function esc(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function renderSectionHTML(title, items, startIndex = 1) {
  if (!items.length) return "";
  const rows = items
    .map((m, i) => {
      const idx = startIndex + i;
      const t = esc(m.title || m.subject || "Untitled");
      const body = esc(m.message || m.content || "");
      const time = m.created_at || m.createdAt || m.sent_at || m.sentAt || "";
      const img = resolveImageUrl(m.image || m.image_url || m.imageUrl || "");
      const imgEl = img
        ? `<div class="n-img"><img src="${img}" alt="attachment" /></div>`
        : "";
      const ago = esc(timeAgo(time));

      return `
      <div class="n-card">
        <div class="n-index">${idx}</div>
        <div class="n-main">
          <div class="n-row"><span class="n-label">Title</span><div class="n-title">${t}</div></div>
          <div class="n-time">
            ${esc(receivedLine(time))}
            ${ago ? `<span class="n-ago">${ago}</span>` : ""}
          </div>
          <div class="n-row">
            <span class="n-label">Message Content</span>
            <div class="n-body">${body || '<em class="text-muted">No content</em>'}</div>
          </div>
          ${imgEl}
        </div>
      </div>`;
    })
    .join("");
  return `
    <div class="n-section">
      <div class="n-h">${esc(title)}</div>
      ${rows}
    </div>`;
}

function renderMessagesHTMLSplit(list = [], seenAnchorMs = 0) {
  const byNew = list.filter(
    (n) => parseTime(n.created_at || n.sent_at) > seenAnchorMs
  );
  const byOld = list.filter(
    (n) => parseTime(n.created_at || n.sent_at) <= seenAnchorMs
  );

  if (!byNew.length && !byOld.length) {
    return `
      <style>
        .n-wrap{ text-align:left }
        .n-empty{ padding:12px;border:1px solid #eef2f7;border-radius:12px;background:#f8fafc }
      </style>
      <div class="n-wrap">
        <div class="n-empty">
          <strong>No notifications</strong>
          <div class="text-muted" style="margin-top:6px;">You're all caught up.</div>
        </div>
      </div>`;
  }

  // numbering across both sections
  let idx = 1;
  const newHTML = renderSectionHTML(
    `New notification(s) (${byNew.length})`,
    byNew,
    idx
  );
  idx += byNew.length;
  const oldHTML = renderSectionHTML(
    `Earlier notifications (${byOld.length})`,
    byOld,
    idx
  );

  return `
    <style>
      .n-list{ display:flex; flex-direction:column; gap:16px; text-align:left; }
      .n-section{ display:flex; flex-direction:column; gap:12px; }
      .n-h{ font-weight:800; font-size:16px; color:#111827; }
      .n-card{
        display:flex; gap:12px; align-items:flex-start;
        padding:14px; border:1px solid #eef2f7; border-radius:14px; background:#fff;
      }
      .n-index{
        min-width:32px; height:32px; border-radius:50%;
        display:flex; align-items:center; justify-content:center;
        font-weight:700; background:#111827; color:#fff; font-size:14px;
      }
      .n-main{ flex:1; }
      .n-row{ display:grid; grid-template-columns: 140px 1fr; gap:10px; align-items:start; }
      .n-label{
        font-size:12px; color:#6b7280; background:#f3f4f6; border:1px solid #e5e7eb;
        padding:4px 8px; border-radius:999px; white-space:nowrap; height:fit-content;
      }
      .n-title{ font-weight:700; margin-bottom:2px; }
      .n-time{ color:#6b7280; font-size:12px; margin:6px 0 8px 0; display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
      .n-ago{ font-size:11px; color:#374151; background:#eef2ff; border:1px solid #e0e7ff; padding:2px 8px; border-radius:999px; }
      .n-body{ white-space:pre-wrap; line-height:1.4; }
      .n-img{ margin-top:10px; }
      .n-img img{
        width:100%; max-height:260px; object-fit:cover; border-radius:10px; border:1px solid #e5e7eb;
      }
    </style>
    <div class="n-list">
      ${newHTML}${oldHTML}
    </div>`;
}

// ---- component ----
export default function TopBar({ title }) {
  const nav = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useContext(ThemeContext);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [badge, setBadge] = useState(0);

  const token = localStorage.getItem("access_token") || "";
  const userFromStore = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  })();
  const userId =
    userFromStore?.user_id ??
    userFromStore?.id ??
    userFromStore?._id ??
    localStorage.getItem("user_id") ??
    localStorage.getItem("admin_id");

  const [roleState, setRoleState] = useState(() => readRoleKind(userFromStore || {}));

  // Re-read roles when Login fires auth-role-changed in the same tab
  useEffect(() => {
    const onRoleChanged = () => {
      try {
        const u = JSON.parse(localStorage.getItem("user") || "null") || {};
        setRoleState(readRoleKind(u));
      } catch { }
    };
    window.addEventListener("auth-role-changed", onRoleChanged);
    return () => window.removeEventListener("auth-role-changed", onRoleChanged);
  }, []);

  const { isSuper, isAdmin, isFinance, roleLabel } = roleState;

  const iconColor = theme === "dark" ? "#ffffff" : "#111111";
  const toggleBtnClass =
    theme === "dark" ? "btn-outline-light" : "btn-outline-secondary";

  // ---- dynamic title based on path ----
  const pageTitle = useMemo(() => {
    const path = location.pathname || "";

    // FINANCE
    if (path.startsWith("/finance/pg-log")) return "PG Log";

    // ✅ NEW: Due Settlement must be checked BEFORE /finance/wallet (more specific route first)
    if (path.startsWith("/finance/due-settlement")) return "Due Settlement";

    // ✅ NEW: Withdrawals page title
    if (path.startsWith("/finance/withdrawals")) return "Withdrawals";

    if (path.startsWith("/finance/wallet")) return "Wallet";
    if (path.startsWith("/finance/reports")) return "Reports";

    // SERVICES
    if (path.startsWith("/services/food")) return "Food Service";
    if (path.startsWith("/services/mart")) return "Mart Service";
    if (path.startsWith("/services/transport")) return "Transport Service";

    // Services – Events, Dining, Hotel
    if (path.startsWith("/services/events")) return "Events & Entertainment";
    if (path.startsWith("/services/dining")) return "Dining Out";
    if (path.startsWith("/services/hotel")) return "Hotel";

    // Master Management
    if (path.startsWith("/master-management/offers-and-rewards"))
      return "Offers";
    if (path.startsWith("/master-management/points-management"))
      return "Points ";
    if (path.startsWith("/master-management/tax-management"))
      return "Tax ";
    if (path.startsWith("/master-management/platform-fee-management"))
      return "Platform Fee";

    if (path.startsWith("/dashboard")) return "Dashboard";

    return title || "Admin";
  }, [location.pathname, title]);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    fetch(`${API_BASE_URL}/api/profile/${userId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => {
        if (!r.ok) throw new Error("Failed to fetch profile");
        return r.json();
      })
      .then((j) => setProfile(j.data || j))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [userId, token]);

  // poll badge (unseen count since last seen) — runs for admin AND finance
  useEffect(() => {
    if ((!isAdmin && !isFinance) || isSuper || !userId) return;

    const role = isFinance ? "finance" : "admin";
    let stop = false;
    const base = `${NOTIF_BY_USER_BASE}/${encodeURIComponent(userId)}?role=${role}`;

    const pull = async () => {
      try {
        const res = await fetch(base, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const json = res.ok ? await res.json() : null;
        const raw = Array.isArray(json?.notifications) ? json.notifications : [];
        // for finance: show all notifications addressed to them; for admin: filter by admin role
        const list = isFinance ? raw : filterForThisAdmin(raw, userId);
        const lastSeen = Number(localStorage.getItem(SEEN_KEY(userId)) || 0);
        const unseen = list.filter(
          (n) => parseTime(n.created_at || n.sent_at) > lastSeen
        ).length;
        if (!stop) setBadge(unseen);
      } catch {
        if (!stop) setBadge(0);
      }
    };
    pull();
    const t = setInterval(pull, 30000);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, [isAdmin, isFinance, isSuper, userId, token]);

  const handleLogout = () => {
    ["access_token", "refresh_token", "user", "user_id", "admin_id", "is_super", "is_finance"].forEach(
      localStorage.removeItem.bind(localStorage)
    );
    nav("/");
  };

  const avatarSrc =
    (profile?.profile_image &&
      (isAbsoluteUrl(profile.profile_image)
        ? profile.profile_image
        : joinUrl(API_BASE_URL, profile.profile_image))) ||
    DEFAULT_AVATAR;

  const openNotificationsModal = async () => {
    if (!userId) {
      await Swal.fire({
        icon: "error",
        title: "No user id",
        text: "Please login again.",
      });
      return;
    }
    const role = isFinance ? "finance" : "admin";
    const fetchUrl = `${NOTIF_BY_USER_BASE}/${encodeURIComponent(userId)}?role=${role}`;

    const seenAnchor = Number(localStorage.getItem(SEEN_KEY(userId)) || 0);
    setBadge(0);

    try {
      const res = await fetch(fetchUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const json = res.ok ? await res.json() : null;
      const raw = Array.isArray(json?.notifications) ? json.notifications : [];
      // finance: show all their notifications; admin: filter by admin role
      const list = sortByNewest(isFinance ? raw : filterForThisAdmin(raw, userId));

      await Swal.fire({
        title: "Notifications",
        html: renderMessagesHTMLSplit(list, seenAnchor),
        width: "48rem",
        showCloseButton: true,
        showConfirmButton: false,
        focusConfirm: false,
        customClass: {
          popup: "swal-popup",
          title: "swal-title",
          htmlContainer: "swal-html",
        },
        willClose: () => {
          localStorage.setItem(SEEN_KEY(userId), String(Date.now()));
        },
      });
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "Could not load notifications",
        text: String(err?.message || err),
      });
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{pageTitle}</h2>
        <div>Loading user info…</div>
      </div>
    );
  }

  const badgeStyle = ROLE_BADGE_COLOR[roleLabel] || ROLE_BADGE_COLOR["Admin"];
  const displayName = profile?.user_name || userFromStore?.user_name || userFromStore?.name || "Admin";

  return (
    <div className="d-flex justify-content-between align-items-center mb-4">
      <h2>{pageTitle}</h2>

      <div className="d-flex align-items-center gap-3">

        {/* Role badge */}
        <span
          style={{
            background: badgeStyle.bg, color: badgeStyle.text,
            fontSize: 11, fontWeight: 700, padding: "3px 10px",
            borderRadius: 999, letterSpacing: ".3px", whiteSpace: "nowrap",
          }}
        >
          {roleLabel}
        </span>

        {/* Bell — show for admin and finance (not super admin) */}
        {(isAdmin || isFinance) && !isSuper && (
          <button
            className="btn p-0 border-0 bg-transparent position-relative"
            title="Notifications"
            style={{ lineHeight: 0 }}
            onClick={openNotificationsModal}
            aria-label="Notifications"
          >
            <i className="bi bi-bell fs-5" style={{ color: iconColor }} />
            {badge > 0 && (
              <span
                className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                style={{ fontSize: 10 }}
              >
                {badge > 99 ? "99+" : badge}
              </span>
            )}
          </button>
        )}

        {/* Theme toggle */}
        <button
          className={`btn btn-sm ${toggleBtnClass}`}
          onClick={toggleTheme}
          title={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
          aria-label="Toggle theme"
        >
          <i
            className={`bi ${theme === "light" ? "bi-moon-fill" : "bi-sun-fill"}`}
            style={{ color: iconColor }}
          />
        </button>

        {/* Profile dropdown */}
        <div className="dropdown">
          <button
            id="profileDropdown"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            className="btn p-0 border-0 bg-transparent"
            style={{ lineHeight: 0 }}
            aria-label="Profile menu"
          >
            <img
              src={avatarSrc}
              alt="User avatar"
              className="rounded-circle"
              width={AVATAR_SIZE}
              height={AVATAR_SIZE}
              style={{
                width: AVATAR_SIZE, height: AVATAR_SIZE,
                aspectRatio: "1 / 1", borderRadius: "50%",
                objectFit: "cover", display: "block",
                flexShrink: 0, cursor: "pointer",
              }}
              loading="eager"
              decoding="async"
              onError={(e) => {
                if (e.currentTarget.src !== DEFAULT_AVATAR) {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = DEFAULT_AVATAR;
                }
              }}
            />
          </button>

          <ul className="dropdown-menu dropdown-menu-end mt-2" aria-labelledby="profileDropdown">
            {/* User name + role header */}
            <li className="px-3 py-2" style={{ borderBottom: "1px solid #eee" }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{displayName}</div>
              <div style={{ fontSize: 12, color: badgeStyle.bg, fontWeight: 600 }}>{roleLabel}</div>
            </li>
            <li>
              <button className="dropdown-item" onClick={() => nav("/profile")}>
                <i className="bi bi-person me-2" />Profile
              </button>
            </li>
            {/* Settings — only for admin and super admin, not finance */}
            {!isFinance && (
              <li>
                <button className="dropdown-item" onClick={() => nav("/settings")}>
                  <i className="bi bi-gear me-2" />Settings
                </button>
              </li>
            )}
            <li><hr className="dropdown-divider" /></li>
            <li>
              <button className="dropdown-item text-danger" onClick={handleLogout}>
                <i className="bi bi-box-arrow-right me-2" />Logout
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}